import { sanitizeMoney } from "components/bookingServicing/modifyQuoteMath";

/*
  The FLIGHT quote (the price quote of a modification and the refund quote of
  a cancellation). The admin fills it; the partner accepts or rejects it.

    airline   difference + tax + penalty           → `supplier` (what goes to
                                                      the airline; shown, never
                                                      added on top)
    ours      serviceFee + markupFee, gst = 18% of the two (worked out here)
    total     supplier + serviceFee + markupFee + gst − discount
    modify    amountDue    = total
    cancel    refundAmount = bookingPrice − total, not below 0

  Sent as { currency, breakdown: { difference, tax, penalty, supplier,
  serviceFee, markupFee, gst, discount }, total, amountDue, refundAmount,
  bookingPrice (cancel) } — the shape in the backend document
  (flight-modification-flow, §8 Quote APIs). No file yet.
*/

/** GST on the service fee and the markup fee, percent. */
export const FLIGHT_QUOTE_GST_RATE = 18;

/** What the admin types, in form order. `airline` lines make up `supplier`. */
export const FLIGHT_QUOTE_INPUTS = [
  { key: "difference", label: "Fare difference", airline: true },
  { key: "tax", label: "Tax difference", airline: true },
  { key: "penalty", label: "Penalty", airline: true },
  { key: "serviceFee", label: "Service fee" },
  { key: "markupFee", label: "Markup fee" },
  { key: "discount", label: "Discount" },
];

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const amount = (v) => {
  const s = String(v ?? "").trim();
  return s === "" ? 0 : Number(s);
};

/** A fresh draft. With a fare layout (flightFareLayout) the airline lines are
 *  typed per flight and passenger type in `fares`, not in `fees`. */
export const emptyFlightQuoteDraft = (layout = null) => ({
  fees: Object.fromEntries(FLIGHT_QUOTE_INPUTS.map((f) => [f.key, ""])),
  layout: layout || null,
  fares: {},
  attachment: null,
  attachmentError: "",
});

/** Input strings → the full breakdown, with `supplier` and `gst` worked out. */
export const flightQuoteBreakdown = (fees) => {
  const v = Object.fromEntries(FLIGHT_QUOTE_INPUTS.map((f) => [f.key, round2(amount(fees?.[f.key]))]));
  return {
    difference: v.difference,
    tax: v.tax,
    penalty: v.penalty,
    supplier: round2(v.difference + v.tax + v.penalty),
    serviceFee: v.serviceFee,
    markupFee: v.markupFee,
    // With per-passenger rows (draftFees) the GST is their sum, each row
    // rounded on its own — what the backend checks; else 18% of the two fees.
    gst:
      fees?.gst != null && fees.gst !== ""
        ? round2(amount(fees.gst))
        : round2(((v.serviceFee + v.markupFee) * FLIGHT_QUOTE_GST_RATE) / 100),
    discount: v.discount,
  };
};

/*
  The airline's part of a MODIFY quote, per passenger type:

    single PNR   one box for every leg in the request
    split PNR    one box per leg (each leg has its own PNR)

  Each box has a row per passenger type on the booking (Adult / Child /
  Infant): fare difference, tax, penalty, service fee, markup fee — per
  passenger, times the count. GST and the discount stay one for the quote.
  The quote API still takes one breakdown, so the rows are added up into its
  difference / tax / penalty / serviceFee / markupFee (draftFees) when sent.
*/
// The supplier's own fee, typed per row only. The backend takes only the eight
// breakdown keys ("Unknown field" otherwise), so it is sent inside `penalty`
// — still part of `supplier`, no GST — until it has a key of its own.
const SUPPLIER_FEE = { key: "supplierFee", label: "Supplier fee", airline: true };
export const FARE_INPUTS = FLIGHT_QUOTE_INPUTS.filter((f) => f.key !== "discount").flatMap((f) =>
  f.key === "penalty" ? [f, SUPPLIER_FEE] : [f]
);
const PAX_TYPES = ["Adult", "Child", "Infant"];
const PAX_TYPE_OF = { ADT: "Adult", ADULT: "Adult", CHD: "Child", CNN: "Child", CHILD: "Child", INF: "Infant", INFANT: "Infant" };
const paxTypeOf = (p) => PAX_TYPE_OF[String(p?.type || "").toUpperCase()] || "Adult";

/** The booking's flight travellers per type: [{ type: "Adult", count: 2 }, …], only types present. */
export const flightPaxTypes = (booking) => {
  const pax = (booking?.passengers || []).filter((p) => p?._api?.kind === "flight");
  return PAX_TYPES.map((type) => ({ type, count: pax.filter((p) => paxTypeOf(p) === type).length })).filter((t) => t.count);
};

/** The boxes and passenger types a modify request's quote is priced in, or
 *  null when the booking doesn't say (then the quote is one set of amounts). */
export const flightFareLayout = (booking, request) => {
  const legs = (booking?.sectors || []).filter((s) => s?._api?.kind === "flight");
  const asked = (request?.flights || []).map((f) => legs.find((s) => s.id === f.sectorId)).filter(Boolean);
  const types = flightPaxTypes(booking);
  if (!asked.length || !types.length) return null;
  const split = booking?.isSplitTicket === true || new Set(legs.map((s) => s.supplierPnr).filter(Boolean)).size > 1;
  const legLabel = (s) => `${s.direction === "return" ? "Return" : "Onward"} · ${s.route}`;
  // How the backend finds a leg: its transaction (one per PNR) and sector.
  const legRef = (s) => ({
    sectorId: s.id,
    transactionId: s.transactionTid || (request.flights || []).find((f) => f.sectorId === s.id)?.transactionId || null,
    departure: s.fromCode || "",
    arrival: s.toCode || "",
  });
  const groups = split
    ? asked.map((s) => ({ key: s.id, label: legLabel(s), sectors: [legRef(s)] }))
    : [{ key: "all", label: asked.map(legLabel).join(" · "), sectors: asked.map(legRef) }];
  return { split, types, groups };
};

/** GST on one passenger's service fee + markup fee in a box row. */
export const fareRowGst = (draft, group, type) => {
  const row = draft?.fares?.[group]?.[type];
  return round2(((amount(row?.serviceFee) + amount(row?.markupFee)) * FLIGHT_QUOTE_GST_RATE) / 100);
};

/** One passenger's amount in a box row, GST included, before the count. */
export const fareRowTotal = (draft, group, type) =>
  round2(FARE_INPUTS.reduce((acc, f) => acc + amount(draft?.fares?.[group]?.[type]?.[f.key]), 0) + fareRowGst(draft, group, type));

/** The draft's fees, with the airline lines added up from `fares` when it has a layout. */
export const draftFees = (draft) => {
  const layout = draft?.layout;
  if (!layout) return draft?.fees;
  const sum = (key) =>
    layout.groups.reduce(
      (acc, g) => acc + layout.types.reduce((a, t) => a + amount(draft.fares?.[g.key]?.[t.type]?.[key]) * t.count, 0),
      0
    );
  const fees = Object.fromEntries(FARE_INPUTS.map((f) => [f.key, round2(sum(f.key))]));
  fees.penalty = round2(fees.penalty + fees.supplierFee);
  delete fees.supplierFee;
  fees.gst = round2(
    layout.groups.reduce(
      (acc, g) => acc + layout.types.reduce((a, t) => a + fareRowGst(draft, g.key, t.type) * t.count, 0),
      0
    )
  );
  return { ...draft.fees, ...Object.fromEntries(Object.entries(fees).map(([k, v]) => [k, String(v)])) };
};

/** The total of a breakdown, and what it means for this kind of request. */
export const flightQuoteTotals = (b, { cancel = false, bookingPrice } = {}) => {
  const n = (k) => Number(b?.[k]) || 0;
  const supplier = round2(n("difference") + n("tax") + n("penalty"));
  const serviceCharges = round2(n("serviceFee") + n("markupFee") + n("gst"));
  const charges = round2(supplier + serviceCharges);
  const total = round2(charges - n("discount"));
  if (!cancel) return { supplier, serviceCharges, charges, total, amountDue: Math.max(total, 0), refundAmount: 0 };
  const price = round2(bookingPrice);
  return { supplier, serviceCharges, charges, total, amountDue: 0, refundAmount: round2(Math.max(price - total, 0)), bookingPrice: price };
};

/** Field errors, whether the draft can be sent, and the footer hint. */
export const validateFlightQuote = (draft, { cancel = false, bookingPrice } = {}) => {
  const errors = {};
  FLIGHT_QUOTE_INPUTS.forEach((f) => {
    const s = String(draft?.fees?.[f.key] ?? "").trim();
    if (s === "") return;
    const n = Number(s);
    if (!Number.isFinite(n)) errors[f.key] = "Must be a number";
    else if (n < 0) errors[f.key] = "Must be ≥ 0";
  });
  const layout = draft?.layout;
  (layout?.groups || []).forEach((g) =>
    layout.types.forEach(({ type }) =>
      FARE_INPUTS.forEach((f) => {
        const s = String(draft.fares?.[g.key]?.[type]?.[f.key] ?? "").trim();
        if (s !== "" && !(Number(s) >= 0)) errors[`fares.${g.key}.${type}.${f.key}`] = "Must be ≥ 0";
      })
    )
  );
  const t = flightQuoteTotals(flightQuoteBreakdown(draftFees(draft)), { cancel, bookingPrice });
  if (!errors.discount && amount(draft?.fees?.discount) > t.charges) errors.discount = "More than the charges";
  const priceMissing = cancel && !(Number(bookingPrice) > 0);
  if (cancel && !priceMissing && t.total > Number(bookingPrice)) {
    errors.penalty = errors.penalty || "The charges are more than the booking price";
  }
  const empty = !cancel && t.total <= 0;
  const valid = Object.keys(errors).length === 0 && !empty && !priceMissing;
  return {
    errors,
    valid,
    empty,
    priceMissing,
    hint: priceMissing ? "The booking price isn't on this booking" : empty ? "Enter an amount to send a quote" : "",
  };
};

/** The quote body sent with quote/send and quote/update. The form still lets
 *  the admin pick a file, but no file goes to the API yet (no upload API). */
const PAX_TYPE_CODE = { Adult: "ADT", Child: "CHD", Infant: "INF" };

/**
 * The per-passenger rows behind the breakdown, for the backend: one entry per
 * box × passenger type — the legs it covers (sectorId + transactionId), the
 * type (ADT / CHD / INF) and count, the per-passenger amounts and the total.
 * The breakdown stays the sum of these (supplierFee inside its penalty).
 */
export const flightQuoteFares = (draft) => {
  const layout = draft?.layout;
  if (!layout) return null;
  return layout.groups.flatMap((g) =>
    layout.types.map(({ type, count }) => {
      const row = draft.fares?.[g.key]?.[type];
      const each = fareRowTotal(draft, g.key, type);
      return {
        sectors: g.sectors || [],
        paxType: PAX_TYPE_CODE[type],
        count,
        perPax: {
          ...Object.fromEntries(FARE_INPUTS.map((f) => [f.key, round2(amount(row?.[f.key]))])),
          gst: fareRowGst(draft, g.key, type),
          total: each,
        },
        total: round2(each * count),
      };
    })
  );
};

export const buildFlightQuote = (draft, { cancel = false, bookingPrice, currency } = {}) => {
  const breakdown = flightQuoteBreakdown(draftFees(draft));
  const t = flightQuoteTotals(breakdown, { cancel, bookingPrice });
  const fares = cancel ? null : flightQuoteFares(draft);
  return {
    currency: currency || "",
    breakdown,
    total: t.total,
    amountDue: t.amountDue,
    refundAmount: t.refundAmount,
    ...(cancel ? { bookingPrice: t.bookingPrice } : {}),
    ...(fares ? { fares } : {}),
  };
};

/** Is this stored quote a flight quote (this shape)? */
export const isFlightQuote = (quote) => !!quote?.breakdown && "penalty" in quote.breakdown && "supplier" in quote.breakdown;

/** A draft prefilled from a stored quote (Edit quote). */
const PAX_TYPE_OF_CODE = { ADT: "Adult", CHD: "Child", INF: "Infant" };
const typed = (v) => (Number(v) > 0 ? String(v) : "");

/** A sent quote's fares[] back into the form's rows, or null when they don't
 *  fit today's boxes (then the quote opens as one set of amounts). */
const faresDraft = (quote, layout) => {
  const rows = quote?.fares || [];
  if (!layout || !rows.length) return null;
  const fares = {};
  for (const e of rows) {
    const group = layout.split ? e.sectors?.[0]?.sectorId : "all";
    const type = PAX_TYPE_OF_CODE[e.paxType];
    if (!layout.groups.some((g) => g.key === group) || !layout.types.some((t) => t.type === type)) return null;
    fares[group] = { ...fares[group], [type]: Object.fromEntries(FARE_INPUTS.map((f) => [f.key, typed(e.perPax?.[f.key])])) };
  }
  return fares;
};

/** A draft prefilled from a stored quote (Edit quote): its per-passenger rows
 *  when it has them and `layout` is given, else its breakdown. */
export const draftFromFlightQuote = (quote, layout = null) => {
  const fares = faresDraft(quote, layout);
  return {
    fees: Object.fromEntries(
      FLIGHT_QUOTE_INPUTS.map((f) => [f.key, fares && f.key !== "discount" ? "" : typed(quote?.breakdown?.[f.key])])
    ),
    layout: fares ? layout : null,
    fares: fares || {},
    attachment: quote?.attachment || null,
    attachmentError: "",
  };
};

/** What an amount input may hold: digits and one dot. */
export const flightQuoteInput = sanitizeMoney;
